import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const CertificatePage = () => {
  const { grade } = useParams<{ grade: string }>();
  const { user, profile } = useAuth();
  const [studentName, setStudentName] = useState('');
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    // Get student name from profile or email
    if (profile?.full_name) {
      setStudentName(profile.full_name);
    } else if (user?.email) {
      const emailName = user.email.split('@')[0];
      const formattedName = emailName
        .split(/[._-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setStudentName(formattedName);
    }

    // Set current date
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    setCurrentDate(date.toLocaleDateString('en-US', options));
  }, [user, profile]);

  if (!user || profile?.membership_status !== 'ACTIVE') {
    return <Navigate to="/dashboard" replace />;
  }

  const getGradeTitle = (gradeParam: string | undefined) => {
    if (!gradeParam) return 'Mathematics';
    
    const gradeMap: Record<string, string> = {
      'kindergarten': 'Kindergarten Mathematics',
      'grade-1': 'Grade 1 Mathematics',
      'grade-2': 'Grade 2 Mathematics',
      'grade-3': 'Grade 3 Mathematics',
      'grade-4': 'Grade 4 Mathematics',
      'grade-5': 'Grade 5 Mathematics',
      'grade-6': 'Grade 6 Mathematics',
      'grade-7': 'Grade 7 Mathematics',
      'grade-8': 'Grade 8 Mathematics',
      'grade-9': 'Grade 9 Algebra I',
      'grade-10': 'Grade 10 Geometry',
      'grade-11': 'Grade 11 Algebra II',
      'grade-12': 'Grade 12 Pre-Calculus',
    };

    return gradeMap[gradeParam] || 'Mathematics';
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-blue-50">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden fixed top-6 right-6 z-50 flex flex-col gap-3">
        <button
          onClick={handlePrint}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
        >
          <i className="ri-printer-line text-xl"></i>
          Print Certificate
        </button>
        <button
          onClick={() => window.history.back()}
          className="bg-white text-gray-700 px-8 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 flex items-center gap-2"
        >
          <i className="ri-arrow-left-line text-xl"></i>
          Back to Dashboard
        </button>
      </div>

      {/* Print Hint - Hidden when printing */}
      <div className="print:hidden max-w-4xl mx-auto pt-6 px-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
          <div className="text-sm text-blue-800">
            <strong className="font-semibold">Tip:</strong> To save as PDF, click "Print Certificate" and select "Save as PDF" as your printer destination.
          </div>
        </div>
      </div>

      {/* Certificate Container */}
      <div className="flex items-center justify-center min-h-screen p-8 print:p-0">
        <div className="bg-white w-full max-w-4xl aspect-[1.414/1] shadow-2xl print:shadow-none relative overflow-hidden">
          {/* Decorative Border */}
          <div className="absolute inset-0 border-[16px] border-double border-amber-600 m-8"></div>
          <div className="absolute inset-0 border-[2px] border-amber-400 m-12"></div>

          {/* Corner Decorations */}
          <div className="absolute top-6 left-6 w-24 h-24 border-t-4 border-l-4 border-amber-500"></div>
          <div className="absolute top-6 right-6 w-24 h-24 border-t-4 border-r-4 border-amber-500"></div>
          <div className="absolute bottom-6 left-6 w-24 h-24 border-b-4 border-l-4 border-amber-500"></div>
          <div className="absolute bottom-6 right-6 w-24 h-24 border-b-4 border-r-4 border-amber-500"></div>

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full blur-3xl"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-16 py-12">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wider mb-4">
                CERTIFICATE OF ACHIEVEMENT
              </div>
              <h1 className="text-5xl font-serif font-bold text-gray-800 mb-2">
                Math Mastery
              </h1>
              <p className="text-lg text-gray-600 font-light">
                by Augustine Osagie
              </p>
            </div>

            {/* Divider */}
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mb-8"></div>

            {/* Main Text */}
            <div className="text-center mb-8 space-y-6">
              <p className="text-xl text-gray-700 font-light">
                This certifies that
              </p>
              <h2 className="text-4xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-2 border-b-2 border-amber-500">
                {studentName || 'Student Name'}
              </h2>
              <p className="text-xl text-gray-700 font-light leading-relaxed max-w-2xl">
                has successfully completed all requirements and demonstrated mastery in
              </p>
              <h3 className="text-3xl font-serif font-bold text-gray-800">
                {getGradeTitle(grade)}
              </h3>
            </div>

            {/* Date and Seal Section */}
            <div className="flex items-center justify-between w-full max-w-2xl mt-12">
              {/* Date */}
              <div className="text-center">
                <div className="border-t-2 border-gray-800 pt-2 px-8">
                  <p className="text-sm text-gray-600 font-semibold">Date</p>
                  <p className="text-base text-gray-800 font-medium mt-1">{currentDate}</p>
                </div>
              </div>

              {/* Seal */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                  <div className="w-20 h-20 rounded-full border-2 border-white flex items-center justify-center">
                    <div className="text-center">
                      <i className="ri-medal-line text-3xl text-white"></i>
                      <p className="text-xs text-white font-bold mt-1">MASTERY</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-red-600 rounded-full border-2 border-white shadow-md"></div>
              </div>

              {/* Signature */}
              <div className="text-center">
                <div className="border-t-2 border-gray-800 pt-2 px-8">
                  <p className="text-sm text-gray-600 font-semibold">Instructor</p>
                  <p className="text-lg font-serif italic text-gray-800 mt-1">Augustine Osagie</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 text-center">
              <p className="text-xs text-gray-500 tracking-wider">
                EXCELLENCE IN MATHEMATICS EDUCATION
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;